import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { tap, catchError, retry, timeout, finalize } from 'rxjs/operators';

/**
 * API operation modes for different user types
 * 
 * @enum {string}
 */
export enum ApiMode {
  /** Internal support agents - uses /predict, /continue endpoints */
  OPERATOR = 'operator',
  /** External customers - uses /predict-chat, /continue-chat endpoints */
  ENDUSER = 'enduser'
}

export interface ChatHistory {
  question: string;
  answer: string;
  timestamp?: string;
}

export interface PredictRequest {
  actor: string;
  question: string;
  history: ChatHistory[];
}

export interface ContinueRequest extends PredictRequest {
  load_history: boolean;
}

export interface RagResponse {
  conversationID: string;
  promptID: string;
}

export interface PollResponse {
  output: string;
}

export interface IssueDetailPayload {
  conversationID: string;
  question: string;
  answer: string | null;
  timestamp: string;
}

export interface ApiError {
  status: number;
  message: string;
  detail?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RagApiService {
  private readonly API_URL_V1 = '/api/v1';
  private readonly API_URL_V2 = '/api/v2';
  private readonly POLLING_INTERVAL = 2000;
  private readonly MAX_RETRIES = 30;
  private isPolling = false;

  private readonly httpOptions = {
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Referrer-Policy', 'strict-origin-when-cross-origin'),
    withCredentials: true
  };

  constructor(private http: HttpClient) {}

  /**
   * Initiates a new conversation with the RAG API
   * 
   * @param request - The prediction request containing question and history
   * @param mode - API mode determining which endpoint to use (required)
   *               OPERATOR uses /predict, ENDUSER uses /predict-chat
   * @returns Observable of RagResponse with conversationID and promptID
   */
  predict(request: PredictRequest, mode: ApiMode): Observable<RagResponse> {
    const endpoint = mode === ApiMode.ENDUSER ? '/predict-chat' : '/predict';
    const baseUrl = mode === ApiMode.ENDUSER ? this.API_URL_V2 : this.API_URL_V1;
    return this.http.post<RagResponse>(
      `${baseUrl}${endpoint}`,
      request,
      this.httpOptions
    ).pipe(
      tap((response) => {
        if (response?.conversationID) {
          localStorage.setItem('conversation_id', response.conversationID);
        }
        if (response?.promptID) {
          localStorage.setItem('prompt_id', response.promptID);
        }
      }),
      catchError(this.handleError),
      finalize(() => console.log(`Predict request completed (${mode} mode)`))
    );
  }

  /**
   * Continues an existing conversation with the RAG API
   * 
   * @param conversationId - The ID of the existing conversation
   * @param request - The continue request containing question, history, and load_history flag
   * @param mode - API mode determining which endpoint to use (required)
   *               OPERATOR uses /continue, ENDUSER uses /continue-chat
   * @returns Observable of RagResponse with updated promptID
   */
  continue(conversationId: string, request: ContinueRequest, mode: ApiMode): Observable<RagResponse> {
    const endpoint = mode === ApiMode.ENDUSER ? '/continue-chat' : '/continue';
    const baseUrl = mode === ApiMode.ENDUSER ? this.API_URL_V2 : this.API_URL_V1;
    return this.http.post<RagResponse>(
      `${baseUrl}${endpoint}/${conversationId}`,
      request,
      this.httpOptions
    ).pipe(
      tap((response) => {
        if (response?.promptID) {
          localStorage.setItem('prompt_id', response.promptID);
        }
      }),
      catchError(this.handleError),
      finalize(() => console.log(`Continue request completed (${mode} mode)`))
    );
  }


  pollResponse(promptId: string, mode: ApiMode): Observable<PollResponse> {
    if (!promptId) {
      return throwError(() => this.createApiError(400, 'No promptId provided for polling'));
    }

    if (this.isPolling) {
      return throwError(() => this.createApiError(400, 'Polling already in progress'));
    }

    this.isPolling = true;
    const baseUrl = mode === ApiMode.ENDUSER ? this.API_URL_V2 : this.API_URL_V1;

    return this.http.get<PollResponse>(
      `${baseUrl}/response/${promptId}`,
      this.httpOptions
    ).pipe(
      timeout(this.POLLING_INTERVAL * this.MAX_RETRIES),
      retry({
        count: this.MAX_RETRIES,
        delay: (error, attempt) => {
          if (error.status === 404 || error.status === 425) {
            return timer(this.POLLING_INTERVAL);
          }
          throw error;
        },
      }),
      tap(response => {
        if (!response.output) {
          console.warn('Empty response output received');
        }
      }),
      catchError(this.handleError),
      finalize(() => {
        this.isPolling = false;
        console.log('Polling completed');
      })
    );
  }

  getIssueDetails(issueKey: string): Observable<IssueDetailPayload> {
    return this.http.get<IssueDetailPayload>(
      `${this.API_URL_V1}/issue/${issueKey}`,
      this.httpOptions
    ).pipe(
      tap(response => {
        if (response?.conversationID) {
          localStorage.setItem('conversation_id', response.conversationID);
        }
      }),
      catchError(this.handleError),
      finalize(() => console.log('Issue details request completed'))
    );
  }

  storeConversation(conversationId: string | null): void {
    if (conversationId) {
      localStorage.setItem('conversation_id', conversationId);
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('API Error:', error);
    
    let apiError: ApiError;
    
    if (error.error instanceof ErrorEvent) {
      apiError = {
        status: 0,
        message: 'Network error occurred. Please check your connection.',
        detail: error.error.message
      };
    } else {
      apiError = {
        status: error.status,
        message: error.statusText,
        detail: typeof error.error === 'object' && error.error.detail ? 
                error.error.detail : error.message
      };
    }
    
    return throwError(() => apiError);
  }
  
  private createApiError(status: number, message: string, detail?: string): ApiError {
    return { status, message, detail };
  }

  getStoredConversationId(): string | null {
    return localStorage.getItem('conversation_id');
  }

  getStoredPromptId(): string | null {
    return localStorage.getItem('prompt_id');
  }

  clearStoredIds(): void {
    localStorage.removeItem('conversation_id');
    localStorage.removeItem('prompt_id');
  }
}